import { Wrapper } from '@googlemaps/react-wrapper';
import PropTypes from 'prop-types';
import React, {
	createContext,
	useCallback,
	useMemo,
	useReducer,
	useState,
} from 'react';

import { calculateDistance, formatNumber } from '../utils/helpers';

const default_values = {
	addLocation: () => {},
	locations: {},
	map: null,
	matches: [],
	setMap: () => {},
	setMatches: () => {},
};

function reducer(state, action) {
	let new_state = { ...state };

	switch (action.type) {
		case 'error':
			new_state.message = action.message;
			new_state.status = 'error';
			break;
		case 'get_position':
			new_state.status = 'loading';
			break;
		case 'new_position':
			new_state.position = {
				latitude: action.latitude,
				longitude: action.longitude,
			};
			new_state.status = 'finished';
			break;
		case 'reset_position':
			new_state.position = null;
			new_state.status = 'finished';
			break;
		default:
			throw Error(`Invalid action type: ${action?.type}`);
	}

	return new_state;
}

export const MapContext = createContext(default_values);

export const MapProvider = ({ children }) => {
	const [geoLocationState, dispatchGeoLocation] = useReducer(reducer, {
		position: null,
		status: 'pending',
	});
	const [locations, setLocations] = useState({});
	const [map, setMap] = useState();
	const [matches, setMatches] = useState();

	const addLocation = useCallback((data, marker) => {
		setLocations((current) => {
			current[data.location_id] = { data, marker };
			return current;
		});
	}, []);

	const getGeoLocation = useCallback(() => {
		if (navigator.geolocation) {
			dispatchGeoLocation({ type: 'get_position' });
			navigator.geolocation.getCurrentPosition(
				handleGeoLocationSuccess,
				handleGeoLocationError,
			);
		}
	}, []);

	const handleGeoLocationError = (error) => {
		dispatchGeoLocation({ message: error.message, type: 'error' });
	};

	const handleGeoLocationSuccess = (result) => {
		const latitude = result?.coords?.latitude || 0;
		const longitude = result?.coords?.longitude || 0;
		dispatchGeoLocation({ latitude, longitude, type: 'new_position' });
	};

	const hasGeoLocation = useCallback(() => {
		return navigator?.geolocation ? true : false;
	}, []);

	const resetGeoLocation = useCallback(() => {
		dispatchGeoLocation({ type: 'reset_position' });
	}, []);

	const prepared_locations = useMemo(() => {
		if (geoLocationState?.position) {
			for (const key in locations) {
				if (
					locations[key]?.data?.position?.distance ||
					!locations[key]?.data?.position?.latitude ||
					!locations[key]?.data?.position?.longitude
				) {
					continue;
				}

				locations[key].data.position.distance = calculateDistance(
					locations[key].data.position.latitude,
					locations[key].data.position.longitude,
					geoLocationState.position.latitude,
					geoLocationState.position.longitude,
				);

				if (locations[key].data.position.distance < 1) {
					locations[key].data.position.distance_unit = formatNumber(
						locations[key].data.position.distance * 1000,
						{
							maximumFractionDigits: 0,
							style: 'unit',
							unit: 'meter',
						},
					);
				} else {
					locations[key].data.position.distance_unit = formatNumber(
						locations[key].data.position.distance,
						{
							maximumFractionDigits: 0,
							style: 'unit',
							unit: 'kilometer',
						},
					);
				}
			}
		}

		return locations;
	}, [geoLocationState.position, locations]);

	const value = useMemo(() => {
		return {
			addLocation,
			geoLocationState,
			getGeoLocation,
			hasGeoLocation,
			locations: prepared_locations,
			map,
			matches,
			resetGeoLocation,
			setMap,
			setMatches,
		};
	}, [
		addLocation,
		geoLocationState,
		getGeoLocation,
		hasGeoLocation,
		prepared_locations,
		map,
		matches,
		resetGeoLocation,
		setMap,
		setMatches,
	]);

	return (
		<MapContext.Provider value={value}>
			<Wrapper
				apiKey={process.env.GATSBY_GOOGLE_MAPS_KEY}
				libraries={['places']}
			>
				{children}
			</Wrapper>
		</MapContext.Provider>
	);
};

MapProvider.propTypes = {
	children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};
